import { useState, useEffect } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { useTranslation } from "react-i18next";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";

const PayPalPayment = ({ packageId, packageName, amount, currency, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { token, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [clientId, setClientId] = useState(null);
  const [error, setError] = useState(null);
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [finalAmount, setFinalAmount] = useState(amount);

  useEffect(() => {
    fetchPayPalConfig();
  }, []);

  useEffect(() => {
    if (appliedPromo) {
      const discount = amount * (appliedPromo.discount_percent / 100);
      setFinalAmount(Math.round((amount - discount) * 100) / 100);
    } else {
      setFinalAmount(amount);
    }
  }, [appliedPromo, amount]);

  const fetchPayPalConfig = async () => {
    try {
      const response = await axios.get(`${API}/payments/config`);
      setClientId(response.data.client_id);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching PayPal config:", err);
      setError("Failed to load payment system");
      setLoading(false);
    }
  };

  const applyPromoCode = async () => {
    if (!promoCode.trim()) return;
    
    setPromoLoading(true);
    try {
      const response = await axios.post(
        `${API}/promo/validate`,
        { code: promoCode.trim(), package_id: packageId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.valid) {
        setAppliedPromo({
          code: promoCode.trim().toUpperCase(),
          discount_percent: response.data.discount_percent,
          description: response.data.description
        });
        toast.success(`Promo code applied! ${response.data.discount_percent}% off`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid promo code");
      setAppliedPromo(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const removePromoCode = () => {
    setAppliedPromo(null);
    setPromoCode("");
  };

  const createOrder = async () => {
    try {
      const response = await axios.post(
        `${API}/payments/create-paypal-order`,
        { 
          package_id: packageId,
          promo_code: appliedPromo?.code || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.id;
    } catch (err) {
      console.error("Error creating order:", err);
      toast.error(err.response?.data?.detail || "Failed to create order");
      throw err;
    }
  };

  const onApprove = async (data) => {
    try {
      const response = await axios.post(
        `${API}/payments/capture-paypal-order/${data.orderID}`,
        { promo_code: appliedPromo?.code || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.status === "COMPLETED") {
        toast.success(t('payment.successTitle'));
        await refreshUser();
        onSuccess(response.data);
      }
    } catch (err) {
      console.error("Error capturing order:", err);
      toast.error(err.response?.data?.detail || "Payment failed");
    }
  };

  const onError = (err) => {
    console.error("PayPal error:", err);
    toast.error("Payment failed. Please try again.");
  };

  const formatCurrency = (amt) => {
    const symbols = { gbp: '£', usd: '$', eur: '€' };
    return `${symbols[currency.toLowerCase()] || currency} ${amt.toFixed(2)}`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{t('payment.completePayment')}</h2>
            <p className="text-sm text-gray-500">{packageName}</p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500 text-xl"
          >
            ×
          </button>
        </div>

        {/* Amount */}
        <div className="p-6 bg-gradient-to-r from-rose-50 to-pink-50 border-b border-gray-100">
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-1">{t('payment.totalAmount')}</p>
            {appliedPromo ? (
              <>
                <p className="text-lg text-gray-400 line-through">{formatCurrency(amount)}</p>
                <p className="text-3xl font-bold text-green-600">{formatCurrency(finalAmount)}</p>
                <p className="text-sm text-green-600 mt-1">
                  {appliedPromo.discount_percent}% off with {appliedPromo.code}
                </p>
              </>
            ) : (
              <p className="text-3xl font-bold text-rose-600">{formatCurrency(amount)}</p>
            )}
          </div>
        </div>

        {/* Promo Code */}
        <div className="px-6 pt-4">
          {appliedPromo ? (
            <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-xs font-bold">✓</span>
                <span className="text-green-700 font-medium">{appliedPromo.code}</span>
                <span className="text-green-600 text-sm">({appliedPromo.discount_percent}% off)</span>
              </div>
              <button
                onClick={removePromoCode}
                className="text-green-600 hover:text-green-800 text-sm underline"
              >
                Remove
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🏷️</span>
                <input
                  type="text"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  placeholder="Promo code"
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500"
                  data-testid="promo-code-input"
                />
              </div>
              <button
                onClick={applyPromoCode}
                disabled={promoLoading || !promoCode.trim()}
                className="px-4 py-2 bg-rose-100 text-rose-600 rounded-lg hover:bg-rose-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="apply-promo-btn"
              >
                {promoLoading ? (
                  <span className="w-4 h-4 border-2 border-rose-500 border-t-transparent rounded-full animate-spin inline-block"></span>
                ) : "Apply"}
              </button>
            </div>
          )}
        </div>

        {/* Payment Form */}
        <div className="p-6">
          {loading && (
            <div className="flex flex-col items-center justify-center py-8">
              <span className="w-8 h-8 border-3 border-rose-500 border-t-transparent rounded-full animate-spin mb-4"></span>
              <p className="text-gray-600">{t('payment.loadingPaymentForm')}</p>
            </div>
          )}

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* PayPal Buttons */}
          {!loading && clientId && (
            <PayPalScriptProvider 
              options={{ 
                clientId: clientId,
                currency: currency.toUpperCase(),
                intent: "capture"
              }}
            >
              <div data-testid="paypal-buttons">
                <PayPalButtons
                  style={{
                    layout: "vertical",
                    color: "gold",
                    shape: "rect",
                    label: "paypal"
                  }}
                  createOrder={createOrder}
                  onApprove={onApprove}
                  onError={onError}
                  onCancel={() => toast.info("Payment cancelled")}
                />
              </div>
            </PayPalScriptProvider>
          )}

          {/* Security Badges */}
          <div className="mt-6 flex items-center justify-center gap-4 text-xs text-gray-500">
            <div className="flex items-center gap-1">
              <span className="text-sm">🔒</span>
              <span>{t('payment.securePayment')}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-sm">🛡️</span>
              <span>{t('payment.encryptedData')}</span>
            </div>
          </div>

          {/* Payment Methods Info */}
          <div className="mt-6 pt-4 border-t border-gray-100">
            <p className="text-xs text-center text-gray-500 mb-3">
              {t('payment.acceptedMethods')}
            </p>
            <div className="flex justify-center gap-3 items-center">
              <img src="https://www.paypalobjects.com/webstatic/mktg/Logo/pp-logo-100px.png" alt="PayPal" className="h-6" />
              <span className="text-gray-300">|</span>
              <img src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/visa.svg" alt="Visa" className="h-5 opacity-60" />
              <img src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/mastercard.svg" alt="Mastercard" className="h-5 opacity-60" />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 rounded-b-2xl">
          <p className="text-xs text-center text-gray-500">
            {t('payment.termsNotice')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PayPalPayment;
